// TODO: Consider moving below imports to a helpers/utils directory
import { createReceiveFieldRows, createReceiveTableRows } from "@/excel/speckle/receiveFieldRowBuilder";
import {
  assertReceiveOutputSize,
  assertTargetFitsWorksheet,
  countNonEmptyCells,
} from "@/excel/speckle/receiveRangeGeometry";
import { applyReceiveTableFormat } from "@/excel/speckle/receiveTableFormatting";
import { RECEIVE_LAYOUT_CONFIG, type SpeckleWorkbookWriteTarget } from "@/excel/speckle/receiveTypes";
import { applyBasicValueValidations } from "@/excel/speckle/receiveValueValidation";
import type { ExtendedDocument, VisibleExcelProfile } from "@/services/open-api/openApi.types";
import type { SpeckleReceiveResponse } from "@/services/speckle/speckleExcelApi";

type SpeckleReceiveWriteResult = {
  target: SpeckleWorkbookWriteTarget;
  rowCount: number;
  columnCount: number;
};

export class ReceiveWriteConfirmationRequiredError extends Error {
  public readonly code = "RECEIVE_WRITE_CONFIRMATION_REQUIRED";

  public constructor(
    public readonly target: SpeckleWorkbookWriteTarget,
    public readonly nonEmptyCellCount: number
  ) {
    super("Receive requires confirmation before writing to Excel.");
    this.name = "ReceiveWriteConfirmationRequiredError";
  }
}

async function getSelectedWriteTarget(context: Excel.RequestContext): Promise<SpeckleWorkbookWriteTarget> {
  const selectedRange = context.workbook.getSelectedRange();

  selectedRange.load(["address", "rowIndex", "columnIndex"]);
  selectedRange.worksheet.load("name");

  await context.sync();

  return {
    worksheetName: selectedRange.worksheet.name,
    startRow: selectedRange.rowIndex,
    startColumn: selectedRange.columnIndex,
    selectedAddress: selectedRange.address,
  };
}

async function resolveTargetRange(
  context: Excel.RequestContext,
  rowCount: number,
  columnCount: number,
  rawTarget?: SpeckleWorkbookWriteTarget
): Promise<{
  worksheet: Excel.Worksheet;
  range: Excel.Range;
  target: SpeckleWorkbookWriteTarget;
}> {
  const target = rawTarget ?? (await getSelectedWriteTarget(context));

  assertTargetFitsWorksheet(target, rowCount, columnCount);

  const worksheet = context.workbook.worksheets.getItem(target.worksheetName);
  const range = worksheet.getRangeByIndexes(target.startRow, target.startColumn, rowCount, columnCount);

  range.load("address");
  await context.sync();

  target.targetAddress = range.address;

  return { worksheet, range, target };
}

async function getTargetNonEmptyCellCount(
  context: Excel.RequestContext,
  range: Excel.Range,
  skip: boolean
): Promise<number> {
  if (skip) {
    return 0;
  }

  range.load("values");
  await context.sync();

  return countNonEmptyCells(range.values as unknown[][]);
}

export async function writeSpeckleReceiveResponseToWorkbook(
  response: SpeckleReceiveResponse,
  excelProfile: VisibleExcelProfile,
  apiSchema: ExtendedDocument | undefined,
  confirmed = false,
  target?: SpeckleWorkbookWriteTarget
): Promise<SpeckleReceiveWriteResult> {
  return await Excel.run(async (context) => {
    const fieldRows = createReceiveFieldRows(response, excelProfile, apiSchema);
    const tableRows = createReceiveTableRows(fieldRows);
    const columnCount = RECEIVE_LAYOUT_CONFIG.columnCount;

    assertReceiveOutputSize(tableRows.length, columnCount);

    const resolvedTarget = await resolveTargetRange(context, tableRows.length, columnCount, target);

    const nonEmptyCellCount = await getTargetNonEmptyCellCount(context, resolvedTarget.range, confirmed);

    if (!confirmed && nonEmptyCellCount > 0) {
      throw new ReceiveWriteConfirmationRequiredError(resolvedTarget.target, nonEmptyCellCount);
    }

    resolvedTarget.range.dataValidation.clear();
    resolvedTarget.range.clear(Excel.ClearApplyTo.formats);
    resolvedTarget.range.values = tableRows;

    applyReceiveTableFormat(resolvedTarget.worksheet, resolvedTarget.target, fieldRows);

    resolvedTarget.range.format.autofitColumns();

    applyBasicValueValidations(resolvedTarget.worksheet, resolvedTarget.target, fieldRows);

    await context.sync();

    return {
      target: resolvedTarget.target,
      rowCount: tableRows.length,
      columnCount,
    };
  });
}
